import { createContext, useContext, useState, useEffect, useRef } from 'react'
import { supabase, AUTH_REDIRECT_URL, isNative, readCachedSupabaseUser } from '../lib/supabase'
import { queryClient } from '../lib/queryClient'
import logger from '../utils/logger'
import { Sentry } from '../lib/sentry'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  // Hydrate synchronously from Supabase's localStorage session so a resumed
  // app paints cached UI on first render — no LoadingScreen flash. If the
  // cached session has actually expired, the next API call returns 401 and
  // the existing sessionExpired flow handles it.
  const cachedUser = readCachedSupabaseUser()
  const [user, setUser] = useState(cachedUser)
  const [loading, setLoading] = useState(!cachedUser)
  const [sessionExpired, setSessionExpired] = useState(false)
  const [passwordRecovery, setPasswordRecovery] = useState(false)
  const [playerLinking, setPlayerLinking] = useState(false)
  const initialized = useRef(false)
  const linkingInProgress = useRef(false)

  useEffect(() => {
    // Use onAuthStateChange exclusively — avoids race condition where
    // getSession() resolves before PKCE code exchange on OAuth redirects.
    // INITIAL_SESSION fires after URL detection is complete.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      logger.auth('Auth event', { event, hasSession: !!session, userId: session?.user?.id })

      // Only update user from events that definitively change auth state.
      // During resume, Supabase may fire events with session=null while the
      // token is being refreshed — setting user to null would flash the login screen.
      if (session?.user) {
        setUser(session.user)
        try { Sentry?.setUser?.({ id: session.user.id }) } catch { /* ignore */ }
      } else if (event === 'SIGNED_OUT' || event === 'INITIAL_SESSION') {
        setUser(null)
        try { Sentry?.setUser?.(null) } catch { /* ignore */ }
      }

      if (event === 'INITIAL_SESSION') {
        initialized.current = true
        setLoading(false)
      }

      if (event === 'PASSWORD_RECOVERY') {
        setPasswordRecovery(true)
      }

      if (event === 'SIGNED_OUT') {
        // Session was revoked or refresh token expired
        if (initialized.current && !session) {
          setSessionExpired(true)
        }
      }

      // Update last_platform on every login or app open (skip if unchanged)
      // and record activity for DAU/WAU/MAU tracking
      if (session?.user && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION')) {
        const platform = window.Capacitor?.isNativePlatform?.()
          ? window.Capacitor.getPlatform()
          : 'web'
        if (session.user.user_metadata?.last_platform !== platform) {
          supabase.auth.updateUser({ data: { last_platform: platform } })
        }
        // Record daily activity (idempotent — one row per user per day)
        Promise.resolve(supabase.rpc('record_user_activity')).catch(() => {})
      }

      // Clear expired-session banner on any successful auth event
      if (session?.user && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
        setSessionExpired(false)
      }

      // Auto-link player records on sign-in only (not on token refresh —
      // refreshes happen on every app resume and would cause a blank screen
      // while the RPC runs because playerLinking disables the match query).
      if (session?.user && event === 'SIGNED_IN') {
        if (!linkingInProgress.current) {
          linkingInProgress.current = true
          setPlayerLinking(true)
          supabase.rpc('link_player_by_email').then(({ error }) => {
            linkingInProgress.current = false
            setPlayerLinking(false)
            if (error) logger.warn('Failed to link player by email: ' + error.message)
            queryClient.invalidateQueries({ queryKey: ['my-player'] })
            queryClient.invalidateQueries({ queryKey: ['match'] })
            queryClient.invalidateQueries({ queryKey: ['matches'] })
          })
        }
      }
    })

    // Periodic session health check — detect expired sessions that
    // Supabase's auto-refresh missed (e.g. network was down during refresh)
    const healthCheck = setInterval(async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session && initialized.current && user) {
        logger.auth('Session health check: no active session')
        logger.capture(new Error('Session health check failed — no active session'), {
          hadUser: true, isOnline: navigator.onLine,
        })
        setUser(null)
        setSessionExpired(true)
      }
    }, 5 * 60 * 1000) // every 5 minutes

    return () => {
      subscription.unsubscribe()
      clearInterval(healthCheck)
    }
  }, [])

  const signUp = async (email, password, firstName, lastName, captchaToken) => {
    const displayName = `${firstName} ${lastName}`
    const options = { data: { display_name: displayName, first_name: firstName, last_name: lastName } }
    if (captchaToken) {
      options.captchaToken = captchaToken
    }
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options,
    })
    return { error }
  }

  const signIn = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    return { error }
  }

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    return { error }
  }

  const signInWithProvider = async (provider) => {
    const options = {
      redirectTo: AUTH_REDIRECT_URL,
      skipBrowserRedirect: isNative,
    }
    if (provider === 'google') {
      options.queryParams = { access_type: 'offline', prompt: 'consent' }
    }
    if (provider === 'apple') {
      options.scopes = 'name email'
    }
    const { data, error } = await supabase.auth.signInWithOAuth({ provider, options })

    // On native, open the OAuth URL in the system browser (SFSafariViewController)
    // instead of navigating the webview. The deep link handler in capacitorAuth.js
    // will catch the redirect back to com.press2dn.app://auth/callback.
    if (isNative && data?.url) {
      const { Browser } = await import('@capacitor/browser')
      await Browser.open({ url: data.url, presentationStyle: 'popover' })
    }

    return { error }
  }

  const resetPassword = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: AUTH_REDIRECT_URL,
    })
    return { error }
  }

  const updatePassword = async (newPassword) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    if (!error) setPasswordRecovery(false)
    return { error }
  }

  const clearPasswordRecovery = () => setPasswordRecovery(false)

  const dismissSessionExpired = () => setSessionExpired(false)

  return (
    <AuthContext.Provider value={{ user, loading, playerLinking, sessionExpired, dismissSessionExpired, passwordRecovery, clearPasswordRecovery, signUp, signIn, signInWithProvider, signOut, resetPassword, updatePassword }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}
